import {useState} from 'react';
import {Button, Modal} from '@goproblems/ui';

import {
    hideModal,
    initRootNode,
    initSgf,
    useDispatch,
    useSelector,
} from '@utils';
import {MODAL_GLOBAL_BOARD_SETTINGS} from '@utils';
import {Sgf} from 'ghostban';
import {boardSlice} from '@reducers';
import {useTranslation} from 'react-i18next';
import {ConfirmModal} from './ConfirmModal';
import {UndoRedoManager} from '@utils/undoRedoManager';
import {BoardSettingsForm} from '@components';

export const GlobalBoardSettingsModal = ({
    name = MODAL_GLOBAL_BOARD_SETTINGS,
    showBoardSize = false,
    showResponseSpeed = true,
    showMoveNumbers = true,
    showClearBoard = false,
    undoRedo,
}: {
    name?: string;
    showBoardSize?: boolean;
    showResponseSpeed?: boolean;
    showMoveNumbers?: boolean;
    showClearBoard?: boolean;
    undoRedo?: UndoRedoManager;
}) => {
    const {t} = useTranslation();
    const show = useSelector(s => s.modalGlobalBoardSettings);
    const dispatch = useDispatch();
    const [clearVisible, setClearVisible] = useState<boolean>(false);

    const handleClose = () => {
        hideModal(name);
    };

    return (
        <>
            <ConfirmModal
                show={clearVisible}
                title={t('board_settings.clear_board_modal_title')}
                description={t('board_settings.clear_board_modal_description')}
                okVariant="danger"
                okText={t('common.clear')}
                onOk={async () => {
                    if (!undoRedo) return;
                    setClearVisible(false);
                    undoRedo.clear();
                    const root = initRootNode();
                    if (!root) return;
                    const sgfText = initSgf();
                    const sgf = new Sgf(sgfText);
                    dispatch(boardSlice.actions.setSgf(sgfText));
                    dispatch(boardSlice.actions.setRoot(sgf.root));
                    dispatch(boardSlice.actions.setCurrentNode(sgf.root));
                    dispatch(boardSlice.actions.setZoom(false));
                    dispatch(boardSlice.actions.forceUpdate());
                }}
            />
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('board_settings.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <BoardSettingsForm
                        showBoardSize={showBoardSize}
                        showResponseSpeed={showResponseSpeed}
                        showMoveNumbers={showMoveNumbers}
                        showClearBoard={false}
                    />
                </Modal.Body>
                <Modal.Footer className="flex justify-between">
                    {showClearBoard && (
                        <Button
                            variant="danger"
                            onClick={() => {
                                setClearVisible(true);
                                hideModal(name);
                            }}>
                            {t('board_settings.clear_board')}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={handleClose}>
                        {t('common.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
